import { useState, useEffect } from 'react';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { RotateCcw, CheckCircle, XCircle, Clock, User, Mail, MapPin, Package, FileText, Calendar, AlertTriangle, Trash2 } from 'lucide-react';
import { Loader } from './ui/Loader';
import { formatDistanceToNow } from '../utils/date-utils';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';


interface Withdrawal {
  id: string;
  name: string;
  email: string;
  address: string;
  postalCode: string;
  city: string;
  orderDate: string;
  productService: string;
  orderNumber?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  processedAt?: string;
  adminNotes?: string;
  userId?: string;
  userName?: string | null;
  userEmail?: string | null;
  userType?: 'talent' | 'employer' | string | null;
  subscription?: 'free' | 'plus' | 'employer' | 'employer_plus' | string | null;
  subscriptionPurchasedAt?: string | null;
  deleteAccount?: boolean; // ✅ GDPR: Account deletion request flag
}

export function AdminWithdrawals({ accessToken, subAdminId }: { accessToken: string; subAdminId?: string }) {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/withdrawal/list`,
        {
          headers: {
            ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWithdrawals(data.withdrawals || []);
      } else {
        toast.error('Kunne ikke laste angreskjemaer');
      }
    } catch (error) {
      devError('Error fetching withdrawals:', error);
      toast.error('Feil ved lasting av angreskjemaer');
    } finally {
      setLoading(false);
    }
  };

  const processWithdrawal = async (withdrawalId: string, action: 'approve' | 'reject', notes?: string) => {
    setProcessing(withdrawalId);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/withdrawal/process`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
          },
          body: JSON.stringify({ withdrawal_id: withdrawalId, action, notes }),
        }
      );

      if (response.ok) {
        toast.success(action === 'approve' ? 'Angreskjema godkjent' : 'Angreskjema avvist');
        await fetchWithdrawals();
      } else {
        toast.error('Kunne ikke behandle angreskjema');
      }
    } catch (error) {
      devError('Error processing withdrawal:', error);
      toast.error('Feil ved behandling av angreskjema');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  const filteredWithdrawals = withdrawals.filter(w => {
    if (filterStatus === 'all') return true;
    return w.status === filterStatus;
  });

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;
  const approvedCount = withdrawals.filter(w => w.status === 'approved').length;
  const rejectedCount = withdrawals.filter(w => w.status === 'rejected').length;

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex justify-between items-center">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">Alle statuser ({withdrawals.length})</option>
          <option value="pending">Avventer ({pendingCount})</option>
          <option value="approved">Godkjent ({approvedCount})</option>
          <option value="rejected">Avvist ({rejectedCount})</option>
        </select>

        <button
          onClick={fetchWithdrawals}
          className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-full transition-all flex items-center gap-2"
          title="Oppdater"
        >
          <RotateCcw className="w-4 h-4" />
          Oppdater
        </button>
      </div>

      {/* Withdrawals List */}
      {filteredWithdrawals.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Ingen angreskjemaer funnet
        </div>
      ) : (
        <div className="space-y-4">
          {filteredWithdrawals.map((withdrawal) => {
            const purchaseDate = withdrawal.subscriptionPurchasedAt ? new Date(withdrawal.subscriptionPurchasedAt) : null;
            const purchaseTimestamp = purchaseDate?.getTime() || null;
            const isValidPurchaseDate = purchaseTimestamp !== null && !Number.isNaN(purchaseTimestamp);
            const daysSincePurchase = isValidPurchaseDate
              ? (Date.now() - (purchaseTimestamp as number)) / (1000 * 60 * 60 * 24)
              : null;
            const withinPeriod = daysSincePurchase !== null && daysSincePurchase <= 14;
            const isPeriodExpired = daysSincePurchase !== null && daysSincePurchase > 14;

            return (
              <div
                key={withdrawal.id}
                className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                  withdrawal.status === 'pending'
                    ? 'border-yellow-200'
                    : withdrawal.status === 'approved'
                    ? 'border-green-200'
                    : 'border-red-200'
                }`}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      withdrawal.status === 'pending'
                        ? 'bg-yellow-100'
                        : withdrawal.status === 'approved'
                        ? 'bg-green-100'
                        : 'bg-red-100'
                    }`}>
                      {withdrawal.status === 'pending' && <Clock className="w-5 h-5 text-yellow-600" />}
                      {withdrawal.status === 'approved' && <CheckCircle className="w-5 h-5 text-green-600" />}
                      {withdrawal.status === 'rejected' && <XCircle className="w-5 h-5 text-red-600" />}
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold">{withdrawal.name}</h3>
                      <p className="text-sm text-gray-500">
                        Sendt {formatDistanceToNow(withdrawal.submittedAt)}
                      </p>
                    </div>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    withdrawal.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : withdrawal.status === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {withdrawal.status === 'pending' && 'Avventer'}
                    {withdrawal.status === 'approved' && 'Godkjent'}
                    {withdrawal.status === 'rejected' && 'Avvist'}
                  </span>
                </div>

                {/* User Info (if available) */}
                {withdrawal.userId && (
                  <div className="mb-4 p-4 bg-purple-50 rounded-xl">
                    <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Brukerinformasjon
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Navn:</span>{' '}
                        <span className="text-gray-900 font-medium">{withdrawal.userName || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">E-post:</span>{' '}
                        <span className="text-gray-900 font-medium">{withdrawal.userEmail || withdrawal.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Brukertype:</span>{' '}
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs capitalize">
                          {withdrawal.userType || 'Ukjent'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Abonnement:</span>{' '}
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs capitalize">
                          {withdrawal.subscription || 'free'}
                        </span>
                      </div>
                      {isValidPurchaseDate && (
                        <div className="md:col-span-2">
                          <span className="text-gray-600">Kjøpsdato:</span>{' '}
                          <span className="text-gray-900 font-medium">
                            {purchaseDate?.toLocaleDateString('nb-NO')}
                          </span>
                          {' '}
                          {withinPeriod && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs ml-2">
                              <CheckCircle className="w-3 h-3" />
                              Innenfor 14-dagers frist
                            </span>
                          )}
                          {isPeriodExpired && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs ml-2">
                              <AlertTriangle className="w-3 h-3" />
                              Angrefristen er utgått
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">E-post</p>
                        <p className="text-sm text-gray-900">{withdrawal.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Adresse</p>
                        <p className="text-sm text-gray-900">
                          {withdrawal.address}<br />
                          {withdrawal.postalCode} {withdrawal.city}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Produkt/tjeneste</p>
                        <p className="text-sm text-gray-900">{withdrawal.productService}</p>
                        {withdrawal.orderNumber && (
                          <p className="text-xs text-gray-500 mt-1">Ordrenr: {withdrawal.orderNumber}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Kjøpsdato</p>
                        <p className="text-sm text-gray-900">
                          {new Date(withdrawal.orderDate).toLocaleDateString('nb-NO')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message */}
                {withdrawal.message && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Melding fra kunde</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{withdrawal.message}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ✅ GDPR: Account Deletion Request Warning */}
                {withdrawal.deleteAccount && (
                  <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                    <div className="flex items-start gap-2">
                      <Trash2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-900 mb-1">
                          ⚠️ Kontosletting forespurt
                        </p>
                        <p className="text-sm text-red-800">
                          Brukeren har bedt om permanent sletting av kontoen sin. Etter godkjenning av denne angrerettssaken, 
                          må kontoen slettes manuelt i Supabase Dashboard (Auth {">"} Users {">"} finn bruker via e-post: <span className="font-mono font-semibold">{withdrawal.email}</span>).
                        </p>
                        {withdrawal.userId && (
                          <p className="text-xs text-red-700 mt-2 font-mono">
                            User ID: {withdrawal.userId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Notes (if processed) */}
                {withdrawal.adminNotes && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-blue-700 mb-1">Admin-notater</p>
                        <p className="text-sm text-blue-900 whitespace-pre-wrap">{withdrawal.adminNotes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processed Info */}
                {withdrawal.processedAt && (
                  <p className="text-xs text-gray-500 mb-4">
                    Behandlet {formatDistanceToNow(withdrawal.processedAt)}
                  </p>
                )}

                {/* Actions (only for pending) */}
                {withdrawal.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const notes = prompt('Notater (valgfritt):');
                        processWithdrawal(withdrawal.id, 'approve', notes || undefined);
                      }}
                      disabled={processing === withdrawal.id}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Godkjenn
                    </button>

                    <button
                      onClick={() => {
                        const notes = prompt('Årsak til avvisning (valgfritt):');
                        processWithdrawal(withdrawal.id, 'reject', notes || undefined);
                      }}
                      disabled={processing === withdrawal.id}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="w-4 h-4" />
                      Avvis
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
